import { message, Skeleton } from "antd";
import { type ReactNode, useCallback, useEffect, useState } from "react";

import { IconLinkSimple, IconShare } from "@btnet/blueprint-icons";
import { BTBottomSheet } from "@btnet/core-components/BTBottomSheet";
import { BTPopover, type BTPopoverPlacement } from "@btnet/core-components/BTPopover";
import { useWebviewContext } from "@btnet/utilities-webview";

import {
    type IEntityRecord,
    type IEntityRecordInsert,
} from "helpers/routing/EntityLinkRouter/EntityLinkRouter.api.types";

import { getJobPickerState } from "utilities/jobPicker/jobPicker";
import { useGetShareLink } from "utilities/sharing/share.utilities";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTIconCopy } from "commonComponents/btWrappers/BTIcon";
import { BTInputCopy } from "commonComponents/btWrappers/BTInputCopy/BTInputCopy";
import { BTLayoutContent } from "commonComponents/btWrappers/BTLayout/BTLayout";
import { BTModal } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTModalLayout } from "commonComponents/btWrappers/BTModal/BTModalLayout";

import "./EntityShareButton.less";

export interface ISharingConfig {
    /**
     * Data necessary to create an IEntityRecord. By providing this prop, you are opting-in to
     * generating a sharable link to this modal.
     * Make sure to implement the correct types in EntityRecordSyncManager.
     */
    entity: IEntityRecordInsert;
    /**
     * Use this to customize how the entity record is fetched.
     * Must return an IEntityRecord type
     */
    overrideGetEntityRecord?: (data: IEntityRecordInsert) => Promise<IEntityRecord>;
}

export interface IBTShareButtonProps {
    sharing?: ISharingConfig;
}

export interface IBTShareButtonModalProps extends IBTShareButtonProps {
    sharing?: ISharingConfig;
    isVisible: boolean;
    onBeforeClose: () => void;
}

export const canShare = (sharing?: ISharingConfig): sharing is ISharingConfig => {
    if (sharing === undefined || getJobPickerState().isTemplateMode) {
        return false;
    }

    return sharing.entity.id > 0;
};

/**
 * Shared copy-link content + fetch behavior for every entity share control. `refetchIfNeeded`
 * fetches the link lazily the first time the control opens.
 */
const useShareLinkContent = (sharing?: ISharingConfig) => {
    const { data, error, refetch } = useGetShareLink(sharing);

    useEffect(() => {
        if (error) {
            console.error(error);
            void message.error("Unable to share at this time.");
        }
    }, [error]);

    const content: ReactNode = !data ? (
        <Skeleton.Input active size="small" className="EntityCopyButton-LoadingSkeleton" />
    ) : (
        <BTInputCopy
            data-testid="shareUrl"
            id="shareUrl"
            value={data}
            onChange={() => {}}
            disabled
        />
    );

    const refetchIfNeeded = useCallback(() => {
        if (!data) {
            void refetch();
        }
    }, [data, refetch]);

    return { content, refetchIfNeeded };
};

const useCopyShareLinkToClipboard = (sharing?: ISharingConfig) => {
    const { data, refetch } = useGetShareLink(sharing);

    return useCallback(async () => {
        const shareLink = data ?? (await refetch()).data;
        if (!shareLink) {
            void message.error("Unable to share at this time.");
            return;
        }

        await navigator.clipboard.writeText(shareLink);
        void message.success("Link copied");
    }, [data, refetch]);
};

interface IEntityShareControlProps {
    sharing?: ISharingConfig;
    /** The share button. On web it anchors the popover; in a webview a tap opens the bottom sheet. */
    children: ReactNode;
    /** Popover placement on web. Ignored in the webview bottom-sheet path. */
    placement: BTPopoverPlacement;
    /** Testid for the popover (web) / trigger wrapper (webview). */
    popoverTestId?: string;
}

/**
 * Renders the share copy-link as an anchored popover (web) or a bottom sheet (mobile webview).
 *
 * In an iOS webview an anchored popover fights the dynamic address bar: repositioning against the
 * animating toolbar causes continuous layout thrash. A bottom sheet isn't anchored to the trigger,
 * so it sidesteps that loop. Web/desktop is unchanged.
 */
const EntityShareControl: React.FunctionComponent<IEntityShareControlProps> = ({
    sharing,
    children,
    placement,
    popoverTestId,
}) => {
    const canShareEntity = canShare(sharing);
    const { content, refetchIfNeeded } = useShareLinkContent(sharing);
    const isWebview = useWebviewContext() !== undefined;
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    // Scope the copy-link's joined input+button styling to this control only, so it never
    // affects other BTInputCopy consumers. Rendered in both the popover and the bottom sheet.
    const copyLink = <div className="EntityShareCopyLink">{content}</div>;

    if (!canShareEntity) {
        return (
            <div className="EntityShareButtonContainer" data-testid="entityShareButtonContainer" />
        );
    }

    if (isWebview) {
        return (
            <div className="EntityShareButtonContainer" data-testid="entityShareButtonContainer">
                {/* Wrapper only forwards taps to open the sheet; the inner button stays the focusable control. */}
                <span
                    className="EntityShareButtonTrigger"
                    data-testid={popoverTestId}
                    onClick={() => {
                        setIsSheetOpen(true);
                        refetchIfNeeded();
                    }}
                >
                    {children}
                </span>
                <BTBottomSheet
                    visible={isSheetOpen}
                    onClose={() => setIsSheetOpen(false)}
                    className="EntityShareBottomSheet"
                >
                    <div className="EntityShareBottomSheet-content flex flex-col gap-sm p-md pb-12">
                        <div className="font-semibold text-fg-default">Copy share link</div>
                        {copyLink}
                    </div>
                </BTBottomSheet>
            </div>
        );
    }

    return (
        <div className="EntityShareButtonContainer" data-testid="entityShareButtonContainer">
            <BTPopover
                data-testid={popoverTestId}
                content={copyLink}
                placement={placement}
                arrowPointAtCenter
                removeSpacing
                widthType="unrestricted"
                onVisibleChange={(visible) => {
                    if (visible) {
                        refetchIfNeeded();
                    }
                }}
                trigger="click"
            >
                {children}
            </BTPopover>
        </div>
    );
};

export const EntityShareButton: React.FunctionComponent<Pick<IBTShareButtonProps, "sharing">> = ({
    sharing,
}) => {
    return (
        <EntityShareControl sharing={sharing} placement="bottom">
            {/* EntityShareControl already wraps this trigger in a click BTPopover. A second popover
                on the same element would overwrite its aria-describedby, so use title instead. */}
            {/* eslint-disable-next-line react/forbid-dom-props */}
            <span title="Copy Share Link">
                <BTButtonDeprecated
                    className="EntityCopyButton"
                    data-testid="entityCopyButton"
                    type="tertiary"
                    icon={<BTIconCopy size={20} />}
                />
            </span>
        </EntityShareControl>
    );
};

export const EntityShareButtonBDS: React.FunctionComponent<
    Pick<IBTShareButtonProps, "sharing"> & {
        id: string;
        copyOnClick?: boolean;
    }
> = ({ sharing, id, copyOnClick = false }) => {
    const copyShareLink = useCopyShareLinkToClipboard(sharing);

    // Same nesting constraint as EntityShareButton above — title, not a second popover. Stays a
    // bare span rather than a fragment so EntityShareControl's BTPopover can clone it.
    const button = (
        // eslint-disable-next-line react/forbid-dom-props
        <span title="Copy share link">
            <BTButton
                id={id}
                data-testid="entityCopyButton"
                type="tertiary"
                aria-label="Copy share link"
                icon={<IconShare />}
                value="copyShareLink"
                onClick={copyOnClick ? () => void copyShareLink() : undefined}
            />
        </span>
    );

    if (copyOnClick) {
        return (
            <div className="EntityShareButtonContainer" data-testid="entityShareButtonContainer">
                {canShare(sharing) && button}
            </div>
        );
    }

    return (
        <EntityShareControl
            sharing={sharing}
            placement="bottomRight"
            popoverTestId="entityShareButtonPopover"
        >
            {button}
        </EntityShareControl>
    );
};

export const EntityShareRollupButtonBDS = ({
    sharing,
    id,
}: Pick<IBTShareButtonProps, "sharing"> & { id: string }) => {
    return (
        <EntityShareControl
            sharing={sharing}
            placement="bottomRight"
            popoverTestId="entityShareButtonPopover"
        >
            <BTPopover content="Copy share link">
                <BTButton
                    id={id}
                    data-testid={id}
                    type="tertiary"
                    aria-label="Copy share link"
                    icon={<IconLinkSimple />}
                />
            </BTPopover>
        </EntityShareControl>
    );
};

export const EntityCopyLinkModal = ({
    isVisible,
    onBeforeClose,
    sharing,
}: IBTShareButtonModalProps) => {
    return (
        <BTModal
            data-testid="entity-link-share-modal"
            className="entity-link-share-modal"
            visible={isVisible}
            width="480px"
            beforeClose={() => onBeforeClose()}
            useModalLayout
        >
            <BTModalLayout
                title="Copy share link"
                modalConfig={{ parentRoute: "", beforeClose: () => onBeforeClose() }}
                modalHeaderClassName="entity-link-share-modal-header"
            >
                <BTLayoutContent>
                    <EntityCopyLinkModalContent sharing={sharing} />
                </BTLayoutContent>
            </BTModalLayout>
        </BTModal>
    );
};

export const EntityCopyLinkModalContent = ({ sharing }: Pick<IBTShareButtonProps, "sharing">) => {
    const { data, error, refetch } = useGetShareLink(sharing);

    useEffect(() => {
        if (error) {
            void message.error("Unable to share at this time.");
        }
    }, [error]);

    useEffect(() => {
        void refetch();
    }, [refetch]);

    let modalContent: React.ReactNode;
    if (!data) {
        modalContent = (
            <Skeleton.Input active size="small" className="EntityCopyButton-LoadingSkeleton" />
        );
    } else {
        modalContent = (
            <BTInputCopy
                data-testid="shareUrl"
                id="shareUrl"
                value={data}
                onChange={() => {}}
                disabled
            />
        );
    }

    return (
        <div className="EntityShareButtonContainer" data-testid="entityShareButtonContainer">
            {modalContent}
        </div>
    );
};
